import { ChangeDetectionStrategy, Component, OnInit, WritableSignal, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { switchMap, of, Observable } from 'rxjs';
import { AssigneeService } from 'src/app/api/assignee.service';
import { IncidentReport, IncidentsService, ViewIncident } from 'src/app/api/incidents.service';
import { LinkableLocationsService } from 'src/app/api/linkable-locations.service';
import { IncidentTripsService } from 'src/app/api/incident-trip.service';
import { WorkflowService } from 'src/app/api/workflow.service';
import { OnAssignValues } from '../assign-incident/assign-incident.component';
import { OnTripLinkValues } from '../link-trip/link-trip.component';
import { IncidentModalService } from '../modals/incident-modal.service';
import { TripDetail } from 'src/app/api/commonInterfaces';
import { DialogService } from 'primeng/dynamicdialog';
import { Dialog } from 'primeng/dialog';
import { OnLinkValues } from '../link-report/link-report.component';
import { MenuItem } from 'primeng/api';

@Component({
    selector: 'app-view-incident',
    templateUrl: './view-incident.component.html',
    styleUrls: ['./view-incident.component.scss'],
    providers: [DialogService],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ViewIncidentComponent implements OnInit {

  incident: WritableSignal<ViewIncident> = signal(this.incidentService.emptyViewIncident);
  reports: WritableSignal<IncidentReport[]> = signal([]);
  trips: WritableSignal<TripDetail[]> = signal([])
  assignee$!: Observable<string>;
  location$!: Observable<string>;
  isClosed: WritableSignal<boolean> = signal(false);
  moreMenuItems: MenuItem[] = [
    {label: "Unlink Trip", command: () => this.onUnlinkTrip() },
    {label: "Unlink Report", command: () => this.onUnlinkReport()}
  ]


  constructor(
    private route: ActivatedRoute,
    private incidentService: IncidentsService,
    private incidentModalService: IncidentModalService,
    private dialogService: DialogService,
    private assigneeService: AssigneeService,
    private tripService: IncidentTripsService,
    private workflowService: WorkflowService,
    private linkableLocations: LinkableLocationsService,

  ) {

  }

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const idString = params.get("id")

      if (idString) {
        const id = Number.parseInt(idString)
        this.loadIncident(id)
      }
    })
  }

  loadIncident(id: number) {
    this.incidentService.getIncident(id).subscribe(incident => {
      this.incident.set(incident)
      this.loadTrips();
      this.loadIncidentReports();
      this.assignee$ = this.getAssigneeName()
      this.location$ = this.getLocationName()
      this.checkIfIncidentClosed();
    });

  }

  loadIncidentReports() {
    this.incidentService.getIncidentReportsForIncident(this.incident().workItemId).subscribe(reports => {
      this.reports.set(reports)
    })
  }

  loadTrips() {
    this.tripService.getTripsForIncidentId(this.incident().workItemId).subscribe(trips => {
      this.trips.set(trips)
    })
  }

  getAssigneeName() {
    return this.assigneeService.getAssigneeName(this.incident().assignedId).pipe(
      switchMap(name => {
        return of(name ? name : "Unassigned");
      })
    );
  }

  getLocationName() {
    return this.linkableLocations.getLocationById(this.incident().locationId).pipe(
      switchMap(location => {
        return of(location ? location.name : "Unspecified");
      })
    )
  }

  getStepName() {
    return this.workflowService.getStepName(this.incident().currentStepId)
  }

  isIncidentClosed(): boolean {
    return this.isClosed();
  }

  private checkIfIncidentClosed() {
    this.workflowService.getWorkflowData().subscribe(workflowData => {
      const workflow = workflowData.workflows.find((w: any) => w.workflowId === this.incident().workflowId);
      const isClosed = workflow ? this.incident().currentStepId === workflow.closedStepId : false;
      this.isClosed.set(isClosed);
    });
  }

  onAssign() {
    this.incidentModalService.showAssignModal(this.dialogService, this.incident().workItemId).onClose.subscribe(
      (result: OnAssignValues | undefined) => {
        if (result === OnAssignValues.AssignSuccess) {
          this.loadIncident(this.incident().workItemId);
        }
      }
    )
  }

  onLinkTrip() {
    this.incidentModalService.showTripLinkModal(this.dialogService, this.incident().workItemId).onClose.subscribe(
      (result: OnTripLinkValues | undefined) => {
        if (result === OnTripLinkValues.LinkSuccess) {
          this.loadTrips();
        }
      }
    )
  }

  onUnlinkTrip() {
    this.incidentModalService.showUnlinkTripModal(this.dialogService, this.incident().workItemId, this.trips()).onClose.subscribe(
      (result: OnTripLinkValues | undefined) => {
        if (result === OnTripLinkValues.LinkSuccess) {
          this.loadTrips();
        }
      }
    )
  }


  onUnlinkReport() {
    this.incidentModalService.showUnlinkReportModal(this.dialogService, this.reports().map(r => r.incident), this.incident().workItemId).onClose.subscribe(
      (result: OnLinkValues | undefined) => {
        if (result === OnLinkValues.LinkSuccess) {
          this.loadIncidentReports();
        }
      }
    )
  }

  onTransition() {

  }

  onTransitionComplete(updatedIncident: ViewIncident) {
    // Update the incident data with the transition result
    this.incident.set(updatedIncident);
    
    // Refresh related data
    this.loadTrips();
    this.loadIncidentReports();
    this.assignee$ = this.getAssigneeName();
    this.location$ = this.getLocationName();
    
    // Check if incident status changed (closed/open)
    this.checkIfIncidentClosed();
  }

  tagsChanged() {
    this.loadIncident(this.incident().workItemId);
  }

}
